import React, { useState } from 'react';
import { Button, Input } from 'lift-ui';
import { Eye, EyeOff } from 'lucide-react';
import PasswordValidator from 'password-validator';
import axios from 'axios';

import { logAdminAction } from '@lift-admin/utils/actionLogTrack';
import Modal, { ModalHeader, ModalBody, ModalFooter } from '../../../../shared/components/modal';
import { Toastify } from 'lift-ui';
import { useNavigate } from 'react-router-dom';
import { LoginAnimatedBackground } from '../ui/LoginAnimatedBackground';

const ForgotPasswordChange = ({ changeUserAction, email }: any) => {
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [otpError, setOtpError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [showLoading, setShowLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const navigation = useNavigate();

  const continueClicked = () => {
    setShowModal(false);
    changeUserAction('login');
  };

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOtp(e.target.value);
    if (otpError) setOtpError('');
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    if (passwordError) setPasswordError('');
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value);
    if (confirmPasswordError) setConfirmPasswordError('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    let passwordSchema = new PasswordValidator();
    passwordSchema
      .is()
      .min(6)
      .is()
      .has()
      .uppercase(1)
      .has()
      .lowercase(1)
      .has()
      .digits(1)
      .has()
      .not()
      .spaces();

    if (!otp) {
      setOtpError('OTP is required');
      setPasswordError('');
      setConfirmPasswordError('');
    } else if (!password) {
      setOtpError('');
      setPasswordError('Password is required');
      setConfirmPasswordError('');
      return;
    } else if (!confirmPassword) {
      setOtpError('');
      setPasswordError('');
      setConfirmPasswordError('Confirm password is required');
      return;
    } else if (!passwordSchema.validate(password)) {
      setOtpError('');
      setPasswordError(
        'Password should be 6+ characters with at least 1 uppercase, 1 lowercase, 1 digit and no spaces'
      );
      setConfirmPasswordError('');
      return;
    } else if (password !== confirmPassword) {
      setOtpError('');
      setPasswordError('');
      setConfirmPasswordError("Passwords don't match");
      return;
    } else {
      setOtpError('');
      setPasswordError('');
      setConfirmPasswordError('');
      setShowLoading(true);

      axios
        .post(
          'https://ltpautomatedpublisherscorecard.uc.r.appspot.com/public/admin/password/update',
          {
            email: email,
            otp: otp,
            password: password,
          }
        )
        .then((res) => {
          if (res.status === 200) {
            Toastify('success', 'Password updated');
            // Best-effort: only records if an admin session exists in storage.
            logAdminAction(`Password reset completed via OTP for ${email}`, {
              url: 'https://ltpautomatedpublisherscorecard.uc.r.appspot.com/public/admin/password/update',
              method: 'POST',
              status: 200,
              category: 'auth',
              action: 'reset_password',
              email,
            });
            navigation('/', { state: { action: 'login' } });
          }
        });
    }
  };

  return (
    <div className="min-h-screen flex min-w-full">
      {/* Left side - Form */}
      <div className="w-full md:w-1/2 bg-white flex flex-col justify-center items-center p-0 min-h-full relative">
        {/* Logo at top-left */}
        <button
          type="button"
          className="absolute top-6 left-8"
          onClick={() => changeUserAction('login')}
          aria-label="Go to login"
        >
          <img
            src="https://storage.googleapis.com/ltp-static-data/lift_assets/platform-logo.svg"
            alt="LIFT"
            className="h-6 w-auto"
          />
        </button>

        {/* Centered form */}
        <div className="max-w-md w-full px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">CHANGE PASSWORD</h1>
            <p className="text-gray-600">Enter your OTP and new password</p>
          </div>

          {/* Email display */}
          <div className="flex items-center justify-between mb-6 p-3 bg-gray-50 rounded-md">
            <p className="text-sm text-gray-700">
              Email: <span className="font-medium">{email}</span>
            </p>
            <button
              type="button"
              className="text-sm font-medium hover:underline"
              onClick={() => changeUserAction('forgot-pwd')}
            >
              Change
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div>
              <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-1">
                OTP
              </label>
              <Input
                id="otp"
                type="text"
                value={otp}
                onChange={handleOtpChange}
                placeholder="ABC123"
                disabled={showLoading}
                className={otpError ? 'border-red-500 focus-visible:ring-red-500' : ''}
              />
              {otpError && <p className="text-red-600 text-sm mt-1">{otpError}</p>}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                New Password
              </label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••••"
                  className={`pr-10 w-full ${passwordError ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                  value={password}
                  onChange={handlePasswordChange}
                  disabled={showLoading}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center justify-center z-10"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {passwordError && <p className="text-red-600 text-sm mt-1">{passwordError}</p>}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password
              </label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="••••••••••"
                  className={`pr-10 w-full ${confirmPasswordError ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  disabled={showLoading}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center justify-center z-10"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {confirmPasswordError && <p className="text-red-600 text-sm mt-1">{confirmPasswordError}</p>}
            </div>

            {/* Submit Button */}
            <Button type="submit" variant="default" className="w-full" disabled={showLoading}>
              {showLoading ? 'Changing Password...' : 'Change Password'}
            </Button>
          </form>

          {/* Back to Login Link */}
          <div className="mt-6 text-center">
            <span className="text-gray-600">Remember your password? </span>
            <button
              type="button"
              onClick={() => changeUserAction('login')}
              className="text-gray-900 font-medium hover:underline"
            >
              Log In
            </button>
          </div>
        </div>
      </div>

      {/* Right side - Animated Background */}
      <LoginAnimatedBackground />

      {/* Success Modal */}
      <Modal show={showModal}>
        <ModalHeader>
          <p>Password reset successful!</p>
        </ModalHeader>
        <ModalBody>
          <p>
            Your password has been reset successfully! Please login again to
            continue.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={continueClicked}>
            Continue
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ForgotPasswordChange;
